import { pipelinesAPI, dealsAPI } from '../../api/kanban';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

export const state = {
  pipelines: [],
  selectedPipelineId: null,
  stagesByPipelineId: {},
  dealsByStageId: {},
  // Quanto cada coluna carregou vs. quanto existe — o que sustenta o rodapé
  // "mostrando X de N" e o botão de carregar mais.
  dealsMetaByStageId: {},
  loadingMoreStageIds: [],
  // Totais agregados do quadro, em fatia PRÓPRIA (não dentro do objeto do
  // estágio): `fetchStages` é usado por telas admin e pela ConversationSheet,
  // que não conhecem os filtros da barra — se os totais morassem no estágio,
  // qualquer uma delas sobrescreveria o número filtrado pelo número cheio, em
  // silêncio, com o filtro ainda ligado na tela.
  boardTotalsByStageId: {},
  boardFilters: {
    q: '',
    ownerId: null,
    teamId: null,
    label: null,
    status: null,
    priority: null,
    // `true` = vista de ARQUIVADAS (só elas), não "incluir arquivadas".
    archived: false,
    sort: 'recent',
  },
  dealsByConversationId: {},
  analyticsByPipelineId: {},
  currentDealDetail: null,
  uiFlags: {
    isFetchingPipelines: false,
    isFetchingBoard: false,
    isMovingDeal: false,
    isFetchingConversationDeals: false,
    isCreatingDeal: false,
    isFetchingAnalytics: false,
    isFetchingDealDetail: false,
    isSavingDeal: false,
    isArchivingDeal: false,
  },
};

export const getters = {
  pipelines: _state => _state.pipelines,
  selectedPipeline: _state =>
    _state.pipelines.find(p => p.id === _state.selectedPipelineId) || null,
  stagesForPipeline: _state => pipelineId =>
    _state.stagesByPipelineId[pipelineId] || [],
  dealsForStage: _state => stageId => _state.dealsByStageId[stageId] || [],
  dealsMetaForStage: _state => stageId =>
    _state.dealsMetaByStageId[String(stageId)] || null,
  isLoadingMoreForStage: _state => stageId =>
    _state.loadingMoreStageIds.includes(String(stageId)),
  boardTotalsForStage: _state => stageId =>
    _state.boardTotalsByStageId[String(stageId)] || null,
  boardFilters: _state => _state.boardFilters,
  hasActiveBoardFilters: _state =>
    !!(
      _state.boardFilters.q ||
      _state.boardFilters.ownerId ||
      _state.boardFilters.teamId ||
      _state.boardFilters.label ||
      _state.boardFilters.status ||
      _state.boardFilters.priority ||
      _state.boardFilters.archived
    ),
  // Resumo do funil = a soma das fases que já estão em mão. Sai dos MESMOS
  // agregados do cabeçalho, então não há como o resumo e as colunas
  // discordarem — e nenhum endpoint novo pra manter em sincronia.
  boardSummary: _state => {
    const totals = Object.values(_state.boardTotalsByStageId);
    return totals.reduce(
      (acc, t) => {
        acc.count += t.count || 0;
        Object.entries(t.valueByCurrency || {}).forEach(([currency, cents]) => {
          acc.valueByCurrency[currency] =
            (acc.valueByCurrency[currency] || 0) + Number(cents || 0);
        });
        return acc;
      },
      { count: 0, valueByCurrency: {} }
    );
  },
  dealsForConversation: _state => conversationId =>
    _state.dealsByConversationId[conversationId] || [],
  analyticsForPipeline: _state => pipelineId =>
    _state.analyticsByPipelineId[pipelineId] || null,
  currentDealDetail: _state => _state.currentDealDetail,
  uiFlags: _state => _state.uiFlags,
};

// Traduz os filtros da barra pros nomes que a API entende. Omite o que está
// vazio — mandar `owner_id=null` faria o backend filtrar por dono nulo.
// Funil escolhido, lembrado entre visitas.
//
// Sem isto o quadro voltava SEMPRE pro primeiro funil da conta: quem trabalha no
// segundo trocava a cada carregamento. Passou a doer mais quando as abas viraram
// um seletor (dois cliques em vez de um), então guardar deixou de ser conforto e
// virou parte da mudança.
//
// Sem escopo por conta de propósito: o id guardado é VALIDADO contra a lista que
// a API devolveu (que já é da conta), então id de outra conta simplesmente não
// casa e cai no default. Uma chave a menos pra manter em sincronia.
const lembrarPipeline = pipelineId =>
  LocalStorage.set(LOCAL_STORAGE_KEYS.KANBAN_LAST_PIPELINE, pipelineId);

const pipelineLembrado = () =>
  Number(LocalStorage.get(LOCAL_STORAGE_KEYS.KANBAN_LAST_PIPELINE)) || null;

const boardFilterParams = ({
  q,
  ownerId,
  teamId,
  label,
  status,
  priority,
  archived,
  sort,
} = {}) => {
  const params = {};
  if (archived) params.archived = 'true';
  if (q) params.q = q;
  if (ownerId) params.owner_id = ownerId;
  if (teamId) params.team_id = teamId;
  if (label) params.label = label;
  if (status) params.status = status;
  if (priority) params.priority = priority;
  if (sort) params.sort = sort;
  return params;
};

// Releitura dos totais COALESCIDA.
//
// Contagem e soma são agregados do banco, então toda escrita os envelhece — e
// os gatilhos chegam em rajada: o realtime manda um evento por card, e criar
// card pelo quadro dispara o evento E o refresh explícito do diálogo (duas
// leituras de `/stages` para uma escrita, medido na tela). Uma campanha criando
// 50 cards mandaria 50 requests.
//
// A janela junta a rajada numa leitura só. Quem espera o resultado (o diálogo)
// recebe a promise da leitura que de fato rodar, não uma que resolve antes.
const TOTALS_REFRESH_DEBOUNCE_MS = 300;
let totalsTimer = null;
let totalsWaiters = [];

const scheduleTotalsRefresh = (dispatch, pipelineId) =>
  new Promise((resolve, reject) => {
    totalsWaiters.push({ resolve, reject });
    clearTimeout(totalsTimer);
    totalsTimer = setTimeout(async () => {
      const waiters = totalsWaiters;
      totalsWaiters = [];
      totalsTimer = null;
      try {
        const stages = await dispatch('fetchBoardStages', pipelineId);
        waiters.forEach(w => w.resolve(stages));
      } catch (error) {
        // Falha em voz alta pra quem estava esperando; quem disparou por
        // realtime é fire-and-forget e não tem a quem contar.
        waiters.forEach(w => w.reject(error));
      }
    }, TOTALS_REFRESH_DEBOUNCE_MS);
  });

// Disparo em segundo plano (drag e realtime): não há a quem devolver o erro,
// mas a consequência é o cabeçalho seguir mostrando número velho — então isso
// não pode passar calado.
const logTotalsFailure = error =>
  // eslint-disable-next-line no-console
  console.error('[kanban] Falha ao reler os totais das fases:', error);

// Upsert de um card no mapa de colunas — a ÚNICA definição da regra.
//
// MERGE, não substituição. Tanto a resposta do `move_stage` quanto o payload do
// realtime são enxutos: trazem as colunas do deal, mas NÃO o snapshot do
// contato, do responsável, o inbox nem a contagem de notas — essas são
// relacionais e sairiam caro em todo broadcast. Substituindo, o card voltava a
// ser título+valor no primeiro evento (ou no primeiro arrasto) e só se
// recompunha no próximo carregamento, sem nada dizendo.
//
// O que vem no evento vence (é o mais novo); o que não vem é preservado.
//
// Coluna que não está no quadro carregado NÃO é criada: evento de outro funil,
// ou movimento feito pelo painel do negócio com o quadro noutro funil, não pode
// inventar uma chave que ninguém renderiza.
const upsertDealInStages = (stagesMap, deal) => {
  const anterior = Object.values(stagesMap)
    .flat()
    .find(d => d.id === deal.id);
  const completo = anterior ? { ...anterior, ...deal } : deal;

  const next = {};
  Object.keys(stagesMap).forEach(stageId => {
    next[stageId] = stagesMap[stageId].filter(d => d.id !== completo.id);
  });
  const targetKey = String(completo.pipeline_stage_id);
  if (next[targetKey]) next[targetKey] = [...next[targetKey], completo];
  return next;
};

// O card cabe no recorte que a barra está mostrando?
//
// O realtime furava o filtro: card que não casa entrava na coluna enquanto o
// cabeçalho — que relê `/stages` COM os filtros — não o contava. A contagem
// voltava a discordar da coluna, agora por outra porta, e o card contradizia o
// próprio chip de filtro na barra.
//
// simplificação: `q` compara por substring em minúsculas, o backend usa ILIKE
// do Postgres. Divergem só em dobra de caixa de locale exótico, e o próximo
// carregamento do quadro corrige.
const matchesBoardFilters = (
  deal,
  { q, ownerId, teamId, label, status, priority, archived } = {}
) => {
  // Vista de arquivadas mostra SÓ arquivadas; a viva, só não arquivadas —
  // desarquivar (deal_updated com archived_at nulo) tira o card da vista de
  // arquivadas pelo mesmo caminho que qualquer filtro que deixou de casar.
  if (!!deal.archived_at !== !!archived) return false;
  if (ownerId && Number(deal.owner_id) !== Number(ownerId)) return false;
  // Time e etiqueta: o evento traz só a última conversa (é o que o card
  // mostra); o servidor casa QUALQUER conversa do card. Aproximação aceita —
  // a releitura seguinte corrige.
  if (teamId && Number(deal.conversation?.team?.id) !== Number(teamId)) {
    return false;
  }
  if (label && !(deal.conversation?.labels || []).includes(label)) return false;
  if (status && deal.status !== status) return false;
  if (priority && deal.priority !== priority) return false;
  if (q) {
    const titulo = String(deal.title || '').toLowerCase();
    if (!titulo.includes(String(q).toLowerCase())) return false;
  }
  return true;
};

// Requests em voo, pra coalescer chamadas concorrentes.
//
// Medido na tela (conta 10, dev): entrar no quadro vindo da conversa disparava
// o board INTEIRO 3 vezes — 3× `stages` + 18× `deals` — porque o painel de
// negócios da conversa e o drawer de criar card chamavam `fetchPipelines` pra
// ter a LISTA de funis, e `fetchPipelines` carregava o board de brinde. Com o
// Puma de dev saturado, 5,7s até o primeiro card. Agora o board só sobe quem
// pede (`board: true`), e chamada repetida com o mesmo alvo pega a promise
// que já está no ar.
let pipelinesInFlight = null;
const boardInFlight = new Map();

export const actions = {
  // `board: true` = depois da lista, deixa um funil em foco e carrega o quadro
  // dele (é o que a página do quadro quer). Sem isso, só a lista — o painel da
  // conversa e o drawer de criar card não precisam de coluna nenhuma.
  async fetchPipelines({ commit, dispatch, state: s }, { board = false } = {}) {
    if (!pipelinesInFlight) {
      commit('SET_UI_FLAG', { isFetchingPipelines: true });
      pipelinesInFlight = pipelinesAPI
        .get()
        .then(({ data }) => {
          commit('SET_PIPELINES', data);
          return data;
        })
        .finally(() => {
          pipelinesInFlight = null;
          commit('SET_UI_FLAG', { isFetchingPipelines: false });
        });
    }
    const data = await pipelinesInFlight;
    if (!board) return data;

    // Se o selectedPipelineId aponta pra um pipeline que nao existe mais
    // (deletado em outra sessao/aba), evita 404 ao buscar o board.
    const currentSelected = s.selectedPipelineId;
    const stillExists = data.some(p => p.id === currentSelected);

    if (currentSelected && stillExists) {
      await dispatch('fetchBoard', currentSelected);
    } else if (data.length) {
      // Retoma o último funil usado; o id só vale se ainda existe nesta
      // conta, senão cai no primeiro.
      const lembrado = pipelineLembrado();
      const alvo = data.some(p => p.id === lembrado) ? lembrado : data[0].id;
      await dispatch('selectPipeline', alvo);
    }
    return data;
  },

  selectPipeline({ commit, dispatch }, pipelineId) {
    commit('SELECT_PIPELINE', pipelineId);
    return dispatch('fetchBoard', pipelineId);
  },

  // Deixa um funil em foco SEM carregar o quadro: a analítica e o calendário
  // só precisam saber qual funil, e o lembrado/primeiro é a mesma regra do
  // quadro. Se já há um em foco (e ele existe), não mexe.
  ensurePipelineSelected({ commit, state: s }) {
    const existe = id => s.pipelines.some(p => p.id === id);
    if (s.selectedPipelineId && existe(s.selectedPipelineId)) return;
    if (!s.pipelines.length) return;
    const lembrado = pipelineLembrado();
    commit('SELECT_PIPELINE', existe(lembrado) ? lembrado : s.pipelines[0].id);
  },

  fetchBoard({ dispatch }, pipelineId) {
    const key = String(pipelineId);
    if (!boardInFlight.has(key)) {
      boardInFlight.set(
        key,
        dispatch('loadBoard', pipelineId).finally(() =>
          boardInFlight.delete(key)
        )
      );
    }
    return boardInFlight.get(key);
  },

  async loadBoard({ commit, dispatch }, pipelineId) {
    commit('SET_UI_FLAG', { isFetchingBoard: true });
    try {
      const stages = await dispatch('fetchBoardStages', pipelineId);
      // Trocar de funil só ACRESCENTAVA chaves: as colunas do funil anterior
      // ficavam no mapa pra sempre, crescendo a cada troca, e a guarda do
      // realtime (`if (!dealsByStageId[stageId]) return`) passava a deixar
      // entrar evento de funil que não está na tela. Podar aqui, e não zerar,
      // porque zerar faria o quadro piscar em branco a cada troca de filtro.
      commit(
        'PRUNE_BOARD_STAGES',
        stages.map(stage => String(stage.id))
      );

      // Uma página POR COLUNA. Antes era uma página só (50 cards) pro funil
      // inteiro, repartida no cliente: as colunas disputavam o mesmo orçamento
      // e, passado o teto, o card sumia do quadro em silêncio. Agora cada
      // coluna tem o seu, e o que sobrar é DECLARADO no rodapé dela.
      await Promise.all(
        stages.map(stage =>
          dispatch('fetchStageDeals', { stageId: stage.id, page: 1 })
        )
      );
    } finally {
      commit('SET_UI_FLAG', { isFetchingBoard: false });
    }
  },

  // Estágios COM os totais do quadro (que respeitam os filtros da barra).
  // Separado do `fetchStages` de propósito — ver o comentário do
  // `boardTotalsByStageId` no state.
  async fetchBoardStages({ commit, state: s }, pipelineId) {
    const { data: stages } = await pipelinesAPI.listStages(pipelineId, {
      // Os agregados varrem todos os deals do funil — só o quadro os pede.
      with_totals: true,
      ...boardFilterParams(s.boardFilters),
    });
    commit('SET_STAGES_FOR_PIPELINE', { pipelineId, stages });
    commit('SET_BOARD_TOTALS', {
      totals: stages.reduce((acc, stage) => {
        acc[String(stage.id)] = {
          count: stage.deals_count || 0,
          valueByCurrency: stage.deals_value_by_currency || {},
        };
        return acc;
      }, {}),
    });
    return stages;
  },

  async fetchStageDeals({ commit, state: s }, { stageId, page = 1 }) {
    const { data } = await dealsAPI.list({
      pipeline_stage_id: stageId,
      page,
      ...boardFilterParams(s.boardFilters),
    });
    const deals = data.deals || [];
    const meta = data.meta || {};
    commit(page === 1 ? 'SET_DEALS_FOR_STAGE' : 'APPEND_DEALS_FOR_STAGE', {
      stageId,
      deals,
    });
    commit('SET_DEALS_META_FOR_STAGE', {
      stageId,
      meta: {
        currentPage: meta.current_page || page,
        totalPages: meta.total_pages || 1,
        totalCount: meta.total_count ?? deals.length,
      },
    });
    return deals;
  },

  async loadMoreStageDeals({ commit, dispatch, state: s }, stageId) {
    const meta = s.dealsMetaByStageId[String(stageId)];
    if (!meta || meta.currentPage >= meta.totalPages) return;

    commit('SET_STAGE_LOADING_MORE', { stageId, value: true });
    try {
      await dispatch('fetchStageDeals', {
        stageId,
        page: meta.currentPage + 1,
      });
    } finally {
      commit('SET_STAGE_LOADING_MORE', { stageId, value: false });
    }
  },

  // Os totais do cabeçalho (contagem e soma) são agregados do banco, não
  // derivados do que a coluna carregou — então mover/criar/arquivar card os
  // deixa velhos e é preciso relê-los. A rajada é coalescida (ver
  // `scheduleTotalsRefresh`); evento de funil que não está na tela é
  // descartado antes de virar request.
  refreshStageTotals({ dispatch, state: s }, pipelineId) {
    const id = pipelineId || s.selectedPipelineId;
    if (!id || id !== s.selectedPipelineId) return Promise.resolve();
    return scheduleTotalsRefresh(dispatch, id);
  },

  // Trocar filtro/ordenação relê o quadro inteiro: as colunas mudam de
  // conteúdo E os totais mudam junto, pelos mesmos parâmetros.
  setBoardFilters({ commit, dispatch, state: s }, filters) {
    commit('SET_BOARD_FILTERS', filters);
    if (!s.selectedPipelineId) return Promise.resolve();
    return dispatch('fetchBoard', s.selectedPipelineId);
  },

  async moveDealToStage(
    { commit, dispatch, state: s },
    { dealId, toStageId, lostReason }
  ) {
    const previous = JSON.parse(JSON.stringify(s.dealsByStageId));
    let movingDeal = null;
    Object.values(s.dealsByStageId).forEach(list => {
      const found = list.find(d => d.id === dealId);
      if (found) movingDeal = found;
    });
    if (!movingDeal) return;

    const optimistic = { ...movingDeal, pipeline_stage_id: toStageId };
    commit(
      'SET_DEALS_BY_STAGE_MAP',
      upsertDealInStages(s.dealsByStageId, optimistic)
    );
    commit('SET_UI_FLAG', { isMovingDeal: true });

    try {
      const { data: updated } = await dealsAPI.moveStage(
        dealId,
        toStageId,
        lostReason
      );
      commit(
        'SET_DEALS_BY_STAGE_MAP',
        upsertDealInStages(s.dealsByStageId, updated)
      );
      // Contagem e soma das duas colunas mudaram; elas vêm do banco.
      dispatch('refreshStageTotals', movingDeal.pipeline_id).catch(
        logTotalsFailure
      );
    } catch (error) {
      commit('SET_DEALS_BY_STAGE_MAP', previous);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isMovingDeal: false });
    }
  },

  // ActionCable: reage ao event 'kanban.event' (ver helper/actionCable.js).
  // Só altera o board atual; fetches externos (analytics, conversation deals)
  // não são invalidados aqui — cada painel controla seu próprio lifecycle.
  handleRealtimeEvent({ commit, dispatch, state: s }, data) {
    const { event_type: eventType } = data;
    switch (eventType) {
      case 'deal_created':
      case 'deal_updated':
      case 'deal_stage_changed': {
        const stageId = String(data.pipeline_stage_id);
        if (!s.dealsByStageId[stageId] && eventType !== 'deal_stage_changed') {
          // stage fora do board carregado — ignora
          return;
        }
        const deal = {
          id: data.deal_id,
          title: data.title,
          pipeline_id: data.pipeline_id,
          pipeline_stage_id: data.pipeline_stage_id,
          contact_id: data.contact_id,
          owner_id: data.owner_id,
          status: data.status,
          value_cents: data.value_cents,
          currency: data.currency,
          priority: data.priority,
          expected_close_at: data.expected_close_at,
          sla_breached_at: data.sla_breached_at,
          position: data.position,
          entered_stage_at: data.entered_stage_at,
          archived_at: data.archived_at,
        };
        // Card que não casa com o filtro ativo SAI da coluna em vez de entrar:
        // ele pode estar lá de antes e ter deixado de casar (mudou de status
        // enquanto o quadro filtra por "ganho", por exemplo).
        if (matchesBoardFilters(deal, s.boardFilters)) {
          commit('UPSERT_DEAL_IN_BOARD', deal);
        } else {
          commit('REMOVE_DEAL_FROM_BOARD', deal.id);
        }
        dispatch('refreshStageTotals', data.pipeline_id).catch(
          logTotalsFailure
        );

        // Se o deal aberto no drawer é o mesmo do evento, refetch pra
        // atualizar transitions, notes, activities e demais dados
        // que não vêm no payload enxuto do broadcast.
        if (s.currentDealDetail?.id === data.deal_id) {
          dispatch('fetchDeal', data.deal_id);
        }
        break;
      }
      // Agendamento nasceu/disparou/cancelou: quem cuida é o módulo da tela de
      // Agendamentos — ele relê se estiver aberto e ignora se não estiver.
      case 'scheduled_action_changed': {
        dispatch('scheduledActions/onRealtimeChange', data, { root: true });
        break;
      }
      // Papel "só os meus cards": o servidor manda só os ids pra quem deixou de
      // enxergar o card (conversa reatribuída, Deals::Visibility). Sai do quadro
      // em qualquer vista e fecha se estiver aberto.
      case 'deal_hidden': {
        commit('REMOVE_DEAL_FROM_BOARD', data.deal_id);
        dispatch('refreshStageTotals', data.pipeline_id).catch(
          logTotalsFailure
        );
        if (s.currentDealDetail?.id === data.deal_id) {
          commit('SET_DEAL_DETAIL', null);
        }
        break;
      }
      case 'deal_archived': {
        // Na vista de arquivadas o card recém-arquivado deveria ENTRAR, mas o
        // payload é só id — ele aparece na próxima releitura. Na vista viva,
        // sai.
        if (s.boardFilters.archived) break;
        commit('REMOVE_DEAL_FROM_BOARD', data.deal_id);
        dispatch('refreshStageTotals', data.pipeline_id).catch(
          logTotalsFailure
        );
        if (s.currentDealDetail?.id === data.deal_id) {
          commit('SET_DEAL_DETAIL', null);
        }
        break;
      }
      default:
        break;
    }
  },

  async fetchConversationDeals({ commit }, conversationId) {
    commit('SET_UI_FLAG', { isFetchingConversationDeals: true });
    try {
      const { data } = await dealsAPI.listByConversation(conversationId);
      const deals = data.deals || [];
      commit('SET_CONVERSATION_DEALS', { conversationId, deals });
    } finally {
      commit('SET_UI_FLAG', { isFetchingConversationDeals: false });
    }
  },

  async fetchAnalytics({ commit }, { pipelineId, from, to }) {
    commit('SET_UI_FLAG', { isFetchingAnalytics: true });
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const { data } = await pipelinesAPI.fetchAnalytics(pipelineId, params);
      commit('SET_ANALYTICS', { pipelineId, data });
      return data;
    } finally {
      commit('SET_UI_FLAG', { isFetchingAnalytics: false });
    }
  },

  async fetchDeal({ commit }, dealId) {
    commit('SET_UI_FLAG', { isFetchingDealDetail: true });
    try {
      const { data } = await dealsAPI.show(dealId);
      commit('SET_DEAL_DETAIL', data);
      return data;
    } finally {
      commit('SET_UI_FLAG', { isFetchingDealDetail: false });
    }
  },

  clearDealDetail({ commit }) {
    commit('SET_DEAL_DETAIL', null);
  },

  async updateDeal({ commit }, { dealId, payload }) {
    commit('SET_UI_FLAG', { isSavingDeal: true });
    try {
      const { data } = await dealsAPI.update(dealId, { deal: payload });
      commit('UPSERT_DEAL_IN_BOARD', data);
      commit('SET_DEAL_DETAIL', data);
      return data;
    } finally {
      commit('SET_UI_FLAG', { isSavingDeal: false });
    }
  },

  async archiveDeal({ commit }, dealId) {
    commit('SET_UI_FLAG', { isArchivingDeal: true });
    try {
      await dealsAPI.archive(dealId);
      commit('REMOVE_DEAL_FROM_BOARD', dealId);
      commit('SET_DEAL_DETAIL', null);
    } finally {
      commit('SET_UI_FLAG', { isArchivingDeal: false });
    }
  },

  // O card desarquivado volta pro quadro VIVO; na vista de arquivadas (a
  // única onde dá pra clicar em desarquivar) ele sai da coluna. Conflito com
  // outro card aberto do contato vem como 422 com a mensagem — quem chama
  // mostra.
  async unarchiveDeal({ commit }, dealId) {
    commit('SET_UI_FLAG', { isArchivingDeal: true });
    try {
      await dealsAPI.unarchive(dealId);
      commit('REMOVE_DEAL_FROM_BOARD', dealId);
      commit('SET_DEAL_DETAIL', null);
    } finally {
      commit('SET_UI_FLAG', { isArchivingDeal: false });
    }
  },

  async createDeal({ commit, dispatch }, payload) {
    commit('SET_UI_FLAG', { isCreatingDeal: true });
    try {
      const { data } = await dealsAPI.create(payload);
      if (payload.conversation_id) {
        await dispatch('fetchConversationDeals', payload.conversation_id);
      }
      return data;
    } finally {
      commit('SET_UI_FLAG', { isCreatingDeal: false });
    }
  },

  async fetchPipeline({ commit }, pipelineId) {
    const { data } = await pipelinesAPI.show(pipelineId);
    commit('UPSERT_PIPELINE', data);
    if (Array.isArray(data.stages)) {
      commit('SET_STAGES_FOR_PIPELINE', {
        pipelineId: data.id,
        stages: data.stages,
      });
    }
    return data;
  },

  async fetchStages({ commit }, pipelineId) {
    const { data } = await pipelinesAPI.listStages(pipelineId);
    commit('SET_STAGES_FOR_PIPELINE', { pipelineId, stages: data });
    return data;
  },

  async createPipeline({ commit }, payload) {
    const { data } = await pipelinesAPI.create({ pipeline: payload });
    commit('UPSERT_PIPELINE', data);
    return data;
  },

  async updatePipeline({ commit }, { id, payload }) {
    const { data } = await pipelinesAPI.update(id, { pipeline: payload });
    commit('UPSERT_PIPELINE', data);
    return data;
  },

  async deletePipeline({ commit, dispatch }, id) {
    await pipelinesAPI.delete(id);
    commit('REMOVE_PIPELINE', id);
    // A releitura das passagens é CONSEQUÊNCIA da exclusão, não parte dela: o
    // funil já foi. Deixá-la propagar fazia o chamador tratar como "falhou ao
    // excluir" e pular a reescolha do funil em foco — quadro sem seletor e sem
    // colunas. Falha aqui só envelhece os selos, e quem a relata é o próprio
    // módulo de passagens.
    return dispatch('kanbanHandoffRules/fetch', null, { root: true }).catch(
      () => null
    );
  },

  async createStage({ commit }, { pipelineId, payload }) {
    const { data } = await pipelinesAPI.createStage(pipelineId, payload);
    commit('UPSERT_STAGE', { pipelineId, stage: data });
    // A coluna nasce VAZIA, não ausente. `handleRealtimeEvent` descarta evento
    // de fase que não está em `dealsByStageId` (guard contra evento de outro
    // funil), e sem esta linha a fase criada pelo painel do quadro renderizava
    // a coluna e engolia todo card que chegasse nela até o próximo
    // carregamento. Antes não doía: criar fase era noutra página, e voltar pro
    // quadro relia tudo.
    commit('SET_DEALS_FOR_STAGE', { stageId: data.id, deals: [] });
    return data;
  },

  async updateStage({ commit }, { pipelineId, stageId, payload }) {
    const { data } = await pipelinesAPI.updateStage(
      pipelineId,
      stageId,
      payload
    );
    commit('UPSERT_STAGE', { pipelineId, stage: data });
    return data;
  },

  async deleteStage({ commit, dispatch }, { pipelineId, stageId }) {
    await pipelinesAPI.deleteStage(pipelineId, stageId);
    commit('REMOVE_STAGE', { pipelineId, stageId });
    // `PipelineStage has_many :handoff_rules_as_source, dependent: :destroy` —
    // apagar a fase apaga no BANCO as passagens que saíam dela. Sem reler, a
    // aba Passagens e o selo da coluna seguiam listando regra morta, clicável,
    // que só falhava na hora de salvar.
    //
    // Consequência, não parte da exclusão (mesmo motivo do `deletePipeline`):
    // a fase já foi. Engolir aqui é seguro porque a falha NÃO some — ela vira
    // `loadError` no módulo de passagens, e a aba mostra o erro em vez de
    // afirmar "nenhuma passagem".
    return dispatch('kanbanHandoffRules/fetch', null, { root: true }).catch(
      () => null
    );
  },

  // Otimista com DESFAZER. O commit adiantado existe porque a lista arrastável
  // não pode piscar de volta na ordem velha enquanto o POST viaja — mas até
  // 2026-09-05 ele era só adiantado: o POST recusando deixava a ordem nova no
  // store pra sempre, e quem tentasse desfazer no componente restaurava a
  // ordem RECUSADA (é ela que `stagesForPipeline` passou a devolver). O quadro
  // ficava descrevendo uma sequência que o banco não tem, sem nada dizendo.
  async reorderStages({ commit, state: s }, { pipelineId, ids }) {
    const anterior = (s.stagesByPipelineId[pipelineId] || []).map(st => st.id);
    commit('REORDER_STAGES', { pipelineId, ids });
    try {
      const { data } = await pipelinesAPI.reorderStages(pipelineId, ids);
      if (Array.isArray(data)) {
        commit('SET_STAGES_FOR_PIPELINE', { pipelineId, stages: data });
      }
      return data;
    } catch (error) {
      commit('REORDER_STAGES', { pipelineId, ids: anterior });
      throw error;
    }
  },
};

export const mutations = {
  SET_UI_FLAG(_state, flags) {
    _state.uiFlags = { ..._state.uiFlags, ...flags };
  },
  SET_PIPELINES(_state, pipelines) {
    _state.pipelines = [...pipelines];
  },
  SELECT_PIPELINE(_state, pipelineId) {
    _state.selectedPipelineId = pipelineId;
    lembrarPipeline(pipelineId);
  },
  SET_STAGES_FOR_PIPELINE(_state, { pipelineId, stages }) {
    _state.stagesByPipelineId = {
      ..._state.stagesByPipelineId,
      [pipelineId]: [...stages],
    };
  },
  SET_DEALS_BY_STAGE_MAP(_state, mapping) {
    _state.dealsByStageId = { ...mapping };
  },
  SET_DEALS_FOR_STAGE(_state, { stageId, deals }) {
    _state.dealsByStageId = {
      ..._state.dealsByStageId,
      [String(stageId)]: [...deals],
    };
  },
  APPEND_DEALS_FOR_STAGE(_state, { stageId, deals }) {
    const key = String(stageId);
    const current = _state.dealsByStageId[key] || [];
    // Dedupe por id: a página seguinte pode repetir um card que o realtime já
    // trouxe pra coluna enquanto o usuário lia.
    const seen = new Set(current.map(d => d.id));
    _state.dealsByStageId = {
      ..._state.dealsByStageId,
      [key]: [...current, ...deals.filter(d => !seen.has(d.id))],
    };
  },
  SET_DEALS_META_FOR_STAGE(_state, { stageId, meta }) {
    _state.dealsMetaByStageId = {
      ..._state.dealsMetaByStageId,
      [String(stageId)]: { ...meta },
    };
  },
  SET_BOARD_TOTALS(_state, { totals }) {
    _state.boardTotalsByStageId = { ...totals };
  },
  SET_BOARD_FILTERS(_state, filters) {
    _state.boardFilters = { ..._state.boardFilters, ...filters };
  },
  SET_STAGE_LOADING_MORE(_state, { stageId, value }) {
    const key = String(stageId);
    const rest = _state.loadingMoreStageIds.filter(id => id !== key);
    _state.loadingMoreStageIds = value ? [...rest, key] : rest;
  },
  UPSERT_DEAL_IN_BOARD(_state, deal) {
    _state.dealsByStageId = upsertDealInStages(_state.dealsByStageId, deal);
  },
  // Mantém só as colunas do funil que está na tela. Preserva as listas já
  // carregadas (nada de piscar) e leva junto o que é indexado por estágio.
  PRUNE_BOARD_STAGES(_state, stageIds) {
    const manter = new Set(stageIds);
    const deals = {};
    const meta = {};
    Object.keys(_state.dealsByStageId).forEach(key => {
      if (manter.has(key)) deals[key] = _state.dealsByStageId[key];
    });
    Object.keys(_state.dealsMetaByStageId).forEach(key => {
      if (manter.has(key)) meta[key] = _state.dealsMetaByStageId[key];
    });
    _state.dealsByStageId = deals;
    _state.dealsMetaByStageId = meta;
    _state.loadingMoreStageIds = _state.loadingMoreStageIds.filter(id =>
      manter.has(id)
    );
  },
  REMOVE_DEAL_FROM_BOARD(_state, dealId) {
    const next = {};
    Object.keys(_state.dealsByStageId).forEach(stageId => {
      next[stageId] = _state.dealsByStageId[stageId].filter(
        d => d.id !== dealId
      );
    });
    _state.dealsByStageId = next;
  },
  SET_DEAL_DETAIL(_state, deal) {
    _state.currentDealDetail = deal ? { ...deal } : null;
  },
  SET_CONVERSATION_DEALS(_state, { conversationId, deals }) {
    _state.dealsByConversationId = {
      ..._state.dealsByConversationId,
      [conversationId]: [...deals],
    };
  },
  SET_ANALYTICS(_state, { pipelineId, data }) {
    _state.analyticsByPipelineId = {
      ..._state.analyticsByPipelineId,
      [pipelineId]: data,
    };
  },
  UPSERT_PIPELINE(_state, pipeline) {
    const index = _state.pipelines.findIndex(p => p.id === pipeline.id);
    const summary = { ...pipeline };
    delete summary.stages;
    if (index === -1) {
      _state.pipelines = [..._state.pipelines, summary];
    } else {
      const next = [..._state.pipelines];
      next.splice(index, 1, { ..._state.pipelines[index], ...summary });
      _state.pipelines = next;
    }
  },
  REMOVE_PIPELINE(_state, pipelineId) {
    _state.pipelines = _state.pipelines.filter(p => p.id !== pipelineId);
    const nextStages = { ..._state.stagesByPipelineId };
    delete nextStages[pipelineId];
    _state.stagesByPipelineId = nextStages;
    if (_state.selectedPipelineId === pipelineId) {
      _state.selectedPipelineId = null;
    }
  },
  UPSERT_STAGE(_state, { pipelineId, stage }) {
    const existing = _state.stagesByPipelineId[pipelineId] || [];
    const index = existing.findIndex(s => s.id === stage.id);
    let next;
    if (index === -1) {
      next = [...existing, stage];
    } else {
      next = [...existing];
      next.splice(index, 1, stage);
    }
    next.sort((a, b) => a.display_order - b.display_order);
    _state.stagesByPipelineId = {
      ..._state.stagesByPipelineId,
      [pipelineId]: next,
    };
  },
  REMOVE_STAGE(_state, { pipelineId, stageId }) {
    const existing = _state.stagesByPipelineId[pipelineId] || [];
    _state.stagesByPipelineId = {
      ..._state.stagesByPipelineId,
      [pipelineId]: existing.filter(s => s.id !== stageId),
    };
  },
  REORDER_STAGES(_state, { pipelineId, ids }) {
    const existing = _state.stagesByPipelineId[pipelineId] || [];
    const byId = new Map(existing.map(s => [s.id, s]));
    const reordered = ids
      .map((id, idx) => {
        const stage = byId.get(id);
        if (!stage) return null;
        return { ...stage, display_order: idx };
      })
      .filter(Boolean);
    _state.stagesByPipelineId = {
      ..._state.stagesByPipelineId,
      [pipelineId]: reordered,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
